import React, { Fragment } from 'react'
import {
  Router,
  Route,
  Redirect,
  IndexRedirect,
  browserHistory,
  applyRouterMiddleware,
  IndexRoute
} from 'react-router'
import { useScroll } from 'react-router-scroll'
import { RouterProvider } from 'context/RouterContext'
import scrollBehavior from './lib/scroll-behavior'
import RouteTracker from './lib/route-tracker'
import { isOnLatest } from './lib/version-checker'
import { track as trackError } from 'lib/utils/errors'
import loadable from 'hoc/Loadable'
import { APP_URL } from './config'
import 'analytics/unsafe-page-call'
import Helmet from 'react-helmet'
import personasRoutes from './containers/Customers/routes'
import functionsRoutes from './containers/Functions/routes'
import OgImage from './open-graph.png'

const UserSettingsApp = loadable(() =>
  import(
    /* webpackChunkName: "UserSettingsApp" */ './containers/UserSettings/UserSettingsApp'
  )
)
const ChangePasswordApp = loadable(() =>
  import(
    /* webpackChunkName: "ChangePasswordApp" */ './containers/UserSettings/ChangePasswordApp'
  )
)
const MfaApp = loadable(() =>
  import(/* webpackChunkName: "MfaApp" */ './containers/Mfa/MfaApp')
)
const MfaSecurityCheckApp = loadable(() =>
  import(
    /* webpackChunkName: "MfaSecurityCheckApp" */ './containers/MfaSecurityCheck/MfaSecurityCheckApp'
  )
)
const WelcomeApp = loadable(() =>
  import(/* webpackChunkName: "Welcome" */ './containers/WelcomeApp')
)
const SetupApp = loadable(() =>
  import(/* webpackChunkName: "SetupApp" */ './containers/Setup')
)
const NotFound = loadable(() =>
  import(/* webpackChunkName: "NotFound" */ 'ui/NotFound')
)
const LoginApp = loadable(() =>
  import(/* webpackChunkName: "LoginApp" */ './containers/LoginApp')
)
const Logout = loadable(() =>
  import(/* webpackChunkName: "Logout" */ './containers/Logout')
)
const VerifyApp = loadable(() =>
  import(/* webpackChunkName: "VerifyApp" */ './containers/Verify')
)
const SignupApp = loadable(() =>
  import(/* webpackChunkName: "SignUpApp" */ './containers/SignUpApp')
)
const StartupSignup = loadable(() =>
  import(
    /* webpackChunkName: "StartupSignup" */ './containers/SignUpApp/StartupSignup'
  )
)
const ForgotApp = loadable(() =>
  import(/* webpackChunkName: "ForgotApp" */ './containers/ForgotApp')
)
const ResetApp = loadable(() =>
  import(/* webpackChunkName: "ResetApp" */ './containers/ResetApp')
)
const ForcedResetApp = loadable(() =>
  import(/* webpackChunkName: "ForcedResetApp" */ './containers/ForcedResetApp')
)
const ReceiptApp = loadable(() =>
  import(/* webpackChunkName: "ReceiptApp" */ './containers/ReceiptApp')
)
const WorkspaceApp = loadable(() =>
  import(/* webpackChunkName: "WorkspaceApp" */ './containers/WorkspaceApp')
)
const IntegrationsCatalogDetailApp = loadable(() =>
  import(
    /* webpackChunkName: "IntegrationsCatalogDetailApp" */ './containers/IntegrationsCatalog/DetailsApp'
  )
)
const DestinationsCatalogApp = loadable(() =>
  import(
    /* webpackChunkName: "DestinationsCatalogApp" */ './containers/IntegrationsCatalogV2/DestinationsCatalogApp'
  )
)
const SourcesCatalogApp = loadable(() =>
  import(
    /* webpackChunkName: "SourcesCatalogApp" */ './containers/IntegrationsCatalogV2/SourcesCatalogApp'
  )
)

const BuildCatalogApp = loadable(() =>
  import(
    /* webpackChunkName: "BuildCatalogApp" */ './containers/IntegrationsCatalogV2/BuildCatalogApp'
  )
)

const WorkspaceOverviewApp = loadable(() =>
  import(
    /* webpackChunkName: "WorkspaceOverviewApp" */ './containers/WorkspaceOverviewApp'
  )
)
const UpgradeFlowApp = loadable(() =>
  import(/* webpackChunkName: "UpgradeFlowApp" */ './containers/UpgradeFlow')
)
const WorkspaceOverviewAppV2 = loadable(() =>
  import(
    /* webpackChunkName: "WorkspaceOverviewAppV2" */ './containers/WorkspaceOverviewAppV2'
  )
)
const WorkspaceQuickStart = loadable(() =>
  import(
    /* webpackChunkName: "WorkspaceQuickStart" */ './containers/WorkspaceQuickStart/v2'
  )
)
const WorkspaceQuickStartDestinationSelection = loadable(() =>
  import(
    /* webpackChunkName: "WorkspaceQuickStartDestinationSelection" */ './containers/WorkspaceQuickStart/v2/DestinationStepSelection'
  )
)
const WorkspaceQuickStartAutoDetect = loadable(() =>
  import(
    /* webpackChunkName: "WorkspaceQuickStartAutoDetect" */ './containers/WorkspaceQuickStart/v2/DestinationDiscovery'
  )
)
const WorkspaceQuickStartDestinationConfig = loadable(() =>
  import(
    /* webpackChunkName: "WorkspaceQuickStartDestinationConfig" */ './containers/WorkspaceQuickStart/v2/DestinationStepConfig'
  )
)
const WorkspaceQuickStartSourceInstall = loadable(() =>
  import(
    /* webpackChunkName: "WorkspaceQuickStartSourceInstall" */ './containers/WorkspaceQuickStart/v2/SourceStepInstall'
  )
)
const WorkspaceSettings = loadable(() =>
  import(
    /* webpackChunkName: "WorkspaceSettings" */ './containers/WorkspaceSettings/App'
  )
)
const WorkspaceSettingsBasicApp = loadable(() =>
  import(
    /* webpackChunkName: "WorkspaceSettingsBasicApp" */ './containers/WorkspaceSettings/BasicApp'
  )
)
const WorkspaceSettingsUsageApp = loadable(() =>
  import(
    /* webpackChunkName: "WorkspaceSettingsUsageApp" */ './containers/WorkspaceSettings/UsageApp'
  )
)
const WorkspaceSettingsAdvancedUsageApp = loadable(() =>
  import(
    /* webpackChunkName: "WorkspaceSettingsAdvancedUsageApp" */ './containers/WorkspaceSettings/AdvancedUsageApp/index.tsx'
  )
)
const WorkspaceSettingsAuditForwardingApp = loadable(() =>
  import(
    /* webpackChunkName: "WorkspaceSettingsAuditForwardingApp" */ './containers/WorkspaceSettings/AuditForwardingApp'
  )
)
const WorkspaceSettingsAccessManagementApp = loadable(() =>
  import(
    /* webpackChunkName: "WorkspaceSettingsTeamPermissionsApp" */ './containers/WorkspaceSettings/AccessManagement/AccessManagementApp'
  )
)
const WorkspaceSettingsGroupsPermissions = loadable(() =>
  import(
    /* webpackChunkName: "WorkspaceSettingsGroupPermissions" */ './components/WorkspaceSettings/Permissions/Groups/index.tsx'
  )
)
const WorkspaceSettingsGroupSetupUsers = loadable(() =>
  import(
    /* webpackChunkName: "WorkspaceSettingsGroupSetupUsers" */ './components/WorkspaceSettings/Permissions/Groups/SetupUsers.tsx'
  )
)
const WorkspaceSettingsCreateGroup = loadable(() =>
  import(
    /* webpackChunkName: "WorkspaceSettingsCreateGroup" */ './components/WorkspaceSettings/Permissions/Groups/CreateGroup.tsx'
  )
)
const WorkspaceSettingsSupportAccessApp = loadable(() =>
  import(
    /* webpackChunkName: "WorkspaceSettingsSupportAccessApp" */ './containers/WorkspaceSettings/SupportAccess/SupportAccessApp'
  )
)
const WorkspaceSettingsNotificationsV2App = loadable(() =>
  import(
    /* webpackChunkName: "WorkspaceSettingsNotificationsApp" */ './containers/WorkspaceSettings/NotificationsAppV2'
  )
)
const WorkspaceSettingsSourcesLabelsApp = loadable(() =>
  import(
    /* webpackChunkName: "WorkspaceSettingsSourcesLabelsApp" */ './containers/WorkspaceSettings/SourcesLabels'
  )
)
const WorkspaceSettingsLabelManagementApp = loadable(() =>
  import(
    /* webpackChunkName: "WorkspaceSettingsLabelManagementApp" */ './containers/WorkspaceSettings/LabelManagement/LabelManagementApp'
  )
)
const WorkspaceSettingsSourcesEnvironmentsApp = loadable(() =>
  import(
    /* webpackChunkName: "WorkspaceSettingsSourcesEnvironmentsApp" */ './containers/WorkspaceSettings/SourcesEnvironmentsApp'
  )
)
const IntegrationHealthNotificationsSidesheet = loadable(() =>
  import(
    /* webpackChunkName: "IntegrationHealthNotificationsSidesheet" */ './containers/WorkspaceSettings/NotificationsAppV2/components/IntegrationHealthNotificationsSidesheet'
  )
)
const WorkspaceSettingsConnectionsApp = loadable(() =>
  import(
    /* webpackChunkName: "WorkspaceSettingsConnectionsApp" */ './containers/WorkspaceSettings/Authentication/ConnectionsApp'
  )
)
const WorkspaceSettingsConnectionEditApp = loadable(() =>
  import(
    /* webpackChunkName: "WorkspaceSettingsConnectionEditApp" */ './containers/WorkspaceSettings/Authentication/ConnectionEditApp'
  )
)
const WorkspaceSettingsConnectionSetupApp = loadable(() =>
  import(
    /* webpackChunkName: "WorkspaceSettingsConnectionSetupApp" */ './containers/WorkspaceSettings/Authentication/ConnectionSetupApp'
  )
)
const WorkspaceSettingsConnectionSetupSelectApp = loadable(() =>
  import(
    /* webpackChunkName: "WorkspaceSettingsConnectionSetupSelectApp" */ './containers/WorkspaceSettings/Authentication/ConnectionSetupSelectApp'
  )
)
const WorkspaceSettingsConnectionSetupConnectionApp = loadable(() =>
  import(
    /* webpackChunkName: "WorkspaceSettingsConnectionSetupConnectionApp" */ './containers/WorkspaceSettings/Authentication/ConnectionSetupConnectionApp'
  )
)
const WorkspaceSettingsConnectionSetupIDPApp = loadable(() =>
  import(
    /* webpackChunkName: "WorkspaceSettingsConnectionSetupIDPApp" */ './containers/WorkspaceSettings/Authentication/ConnectionSetupIDPApp'
  )
)
const WorkspaceSettingsConnectionSetupCompleteApp = loadable(() =>
  import(
    /* webpackChunkName: "WorkspaceSettingsConnectionSetupCompleteApp" */ './containers/WorkspaceSettings/Authentication/ConnectionSetupCompleteApp'
  )
)
const WorkspaceSettingsDomainsApp = loadable(() =>
  import(
    /* webpackChunkName: "WorkspaceSettingsDomainsApp" */ './containers/WorkspaceSettings/Authentication/DomainsApp'
  )
)
const WorkspaceSettingsAdvancedApp = loadable(() =>
  import(
    /* webpackChunkName: "WorkspaceSettingsAdvancedApp" */ './containers/WorkspaceSettings/Authentication/AdvancedApp'
  )
)
const WorkspaceSettingsPlansApp = loadable(() =>
  import(
    /* webpackChunkName: "WorkspaceSettingsPlansApp" */ './containers/WorkspaceSettings/PlansApp'
  )
)
const WorkspaceSettingsCardApp = loadable(() =>
  import(
    /* webpackChunkName: "WorkspaceSettingsCardApp" */ './containers/WorkspaceSettings/CardApp'
  )
)
const WorkspaceSettingsPaymentsApp = loadable(() =>
  import(
    /* webpackChunkName: "WorkspaceSettingsPaymentsApp" */ './containers/WorkspaceSettings/PaymentsApp'
  )
)
const WorkspaceSettingsPlanChangeApp = loadable(() =>
  import(
    /* webpackChunkName: "WorkspaceSettingsPlanChangeApp" */ './containers/WorkspaceSettings/PlanChangeApp'
  )
)
const WorkspaceSettingsPlanChangeSelectApp = loadable(() =>
  import(
    /* webpackChunkName: "WorkspaceSettingsPlanChangeSelectApp" */ './containers/WorkspaceSettings/PlanChangeSelectApp'
  )
)
const WorkspaceSettingsPlanChangeConfirmApp = loadable(() =>
  import(
    /* webpackChunkName: "WorkspaceSettingsPlanChangeConfirmApp" */ './containers/WorkspaceSettings/PlanChangeConfirmApp'
  )
)
const WorkspaceSettingsPlanChangeThanksApp = loadable(() =>
  import(
    /* webpackChunkName: "WorkspaceSettingsPlanChangeThanksApp" */ './containers/WorkspaceSettings/PlanChangeThanksApp'
  )
)
const WorkspaceSettingsDeletionApp = loadable(() =>
  import(
    /* webpackChunkName: "WorkspaceSettingsDeletionApp" */ './containers/WorkspaceSettings/WorkspaceChurn/WorkspaceDeleteApp'
  )
)
const WorkspaceSettingsDowngradeApp = loadable(() =>
  import(
    /* webpackChunkName: "WorkspaceSettingsDowngradeApp" */ './containers/WorkspaceSettings/WorkspaceChurn/WorkspaceDowngradeApp'
  )
)
const WorkspaceSettingsPrivacySecurityApp = loadable(() =>
  import(
    /* webpackChunkName: "WorkspaceSettingsPrivacySecurityApp" */ './containers/WorkspaceSettings/PrivacySecurityApp'
  )
)
const WorkspaceSettingsSuppressedUsersApp = loadable(() =>
  import(
    /* webpackChunkName: "WorkspaceSettingsSuppressedUsersApp" */ './containers/WorkspaceSettings/SuppressedUsersApp'
  )
)
const WorkspaceSettingsDeletionRequestsApp = loadable(() =>
  import(
    /* webpackChunkName: "WorkspaceSettingsDeletionRequestsApp" */ './containers/WorkspaceSettings/DeletionRequestsApp'
  )
)
const WorkspaceSettingsAuditTrailApp = loadable(() =>
  import(
    /* webpackChunkName: "WorkspaceSettingsAuditTrailApp" */ './containers/WorkspaceSettings/AuditTrail/AuditTrailApp'
  )
)
const WorkspaceSettingsDataRetentionApp = loadable(() =>
  import(
    /* webpackChunkName: "WorkspaceSettingsDataRetentionApp" */ './containers/WorkspaceSettings/DataRetentionApp'
  )
)
const WorkspaceSettingsSpacesListApp = loadable(() =>
  import(
    /* webpackChunkName: "WorkspaceSettingsSpacesListApp" */ './containers/WorkspaceSettings/SpacesApp/List'
  )
)
const WorkspaceSettingsSpacesCreateApp = loadable(() =>
  import(
    /* webpackChunkName: "WorkspaceSettingsSpacesCreateApp" */ './containers/WorkspaceSettings/SpacesApp/Create'
  )
)
const WorkspaceDestinationsApp = loadable(() =>
  import(
    /* webpackChunkName: "DestinationsSwitch" */ './containers/DestinationsSwitch'
  )
)
const WorkspacesHome = loadable(() =>
  import(/* webpackChunkName: "WorkspacesHome" */ './containers/WorkspacesHome')
)
const WorkspacesNew = loadable(() =>
  import(/* webpackChunkName: "WorkspacesNew" */ './containers/WorkspacesNew')
)
const WorkspaceRedirect = loadable(() =>
  import(
    /* webpackChunkName: "WorkspaceRedirect" */ './components/WorkspaceRedirect'
  )
)

const EnableApp = loadable(() =>
  import(/* webpackChunkName: "EnableApp" */ './containers/EnableApp')
)
const AuthorizeApp = loadable(() =>
  import(
    /* webpackChunkName: "Authorize/App" */ './containers/Authorize/App.tsx'
  )
)
const SourcesApp = loadable(() =>
  import(/* webpackChunkName: "SourcesSwitch" */ './containers/SourcesSwitch')
)
const LegacySourceSetup = loadable(() =>
  import(
    /* webpackChunkName: "LegacySourceSetup" */ './containers/Sources/SourceSetup/legacy/LegacySourceSetup'
  )
)
const SourceApp = loadable(() =>
  import(/* webpackChunkName: "SourceApp" */ './containers/Sources/SourceApp')
)
const SourceDebugger = loadable(() =>
  import(
    /* webpackChunkName: "SourceDebugger" */ './containers/Sources/SourceDebuggerApp'
  )
)
const SourceFunctionErrors = loadable(() =>
  import(
    /* webpackChunkName: "SourceFunctionErrors" */ './containers/Sources/SourceFunctionErrors'
  )
)

const DestinationDetails = loadable(() =>
  import(
    /* webpackChunkName: "DestinationDetails" */ './containers/Destinations/DestinationDetails'
  )
)
const DestinationOverview = loadable(() =>
  import(
    /* webpackChunkName: "DestinationOverview" */ './containers/Destinations/DestinationOverview'
  )
)
const DestinationConfig = loadable(() =>
  import(
    /* webpackChunkName: "DestinationConfig" */ './containers/Destinations/DestinationConfigApp'
  )
)
const DestinationDebugger = loadable(() =>
  import(
    /* webpackChunkName: "DestinationDebugger" */ './components/Destinations/DestinationDebugger'
  )
)
const DestinationDataTransformations = loadable(() =>
  import(
    /* webpackChunkName: "DestinationDataTransformations" */ './components/Destinations/DestinationDataTransformations/index'
  )
)
const DestinationErrorSheet = loadable(() =>
  import(
    /* webpackChunkName: "DestinationErrorSheet" */ './containers/Destinations/DestinationErrors/ErrorSheet'
  )
)
const DestinationEventDelivery = loadable(() =>
  import(
    /* webpackChunkName: "DestinationEventDelivery" */ './containers/Destinations/DestinationEventDelivery'
  )
)
const SourceOverviewApp = loadable(() =>
  import(
    /* webpackChunkName: "SourceOverviewApp" */ './containers/Sources/SourceOverview/SourceOverviewApp'
  )
)
const SourceOverviewRun = loadable(() =>
  import(
    /* webpackChunkName: "SourceOverviewRun" */ './containers/Sources/SourceOverview/SourceOverviewRun'
  )
)
const SummariesPerEventApp = loadable(() =>
  import(
    /* webpackChunkName: "SummariesPerEventApp" */ './containers/Protocols/Violations/SummariesPerEventApp'
  )
)
const SummariesPerViolationApp = loadable(() =>
  import(
    /* webpackChunkName: "SummariesPerViolationApp" */ './containers/Protocols/Violations/SummariesPerViolationApp'
  )
)
const ProtocolsRootApp = loadable(() =>
  import(
    /* webpackChunkName: "ProtocolsRootApp" */ './containers/Protocols/Root'
  )
)

const PrivacyApp = loadable(() =>
  import(/* webpackChunkName: "PrivacyApp" */ './containers/Privacy')
)

const PrivacyWelcomeApp = loadable(() =>
  import(
    /* webpackChunkName: "PrivacyWelcomeApp" */ './containers/Privacy/Welcome/WelcomeApp'
  )
)

const PrivacyConsentApp = loadable(() =>
  import(
    /* webpackChunkName: "PrivacyConsentApp" */ './containers/Privacy/Consent/ConsentApp'
  )
)

const PrivacyInventoryApp = loadable(() =>
  import(
    /* webpackChunkName: "PrivacyInventoryApp" */ './containers/Privacy/Inventory/InventoryApp'
  )
)

const PrivacyInventorySidesheetApp = loadable(() =>
  import(
    /* webpackChunkName: "PrivacyInventorySidesheetApp" */ './containers/Privacy/Inventory/Mainsheet'
  )
)

const PrivacyDictionaryApp = loadable(() =>
  import(
    /* webpackChunkName: "PrivacyDictionaryApp" */ './containers/Privacy/Dictionary'
  )
)

const PrivacyReviewApp = loadable(() =>
  import(
    /* webpackChunkName: "PrivacyReviewApp" */ './containers/Privacy/Review/ReviewApp'
  )
)

const PrivacyControlsApp = loadable(() =>
  import(
    /* webpackChunkName: "PrivacyControlsApp" */ './containers/Privacy/Controls/ControlsApp'
  )
)

const PrivacyAdvancedControlsApp = loadable(() =>
  import(
    /* webpackChunkName: "PrivacyAdvancedControlsApp" */ './containers/Privacy/AdvancedControls/AdvancedControlsApp'
  )
)

const PrivacyInboxApp = loadable(() =>
  import(
    /* webpackChunkName: "PrivacyInboxApp" */ './containers/Privacy/Inbox/InboxApp'
  )
)

const PrivacyCatalogApp = loadable(() =>
  import(
    /* webpackChunkName: "PrivacyCatalogApp" */ './containers/Privacy/Catalog/CatalogApp'
  )
)

const PrivacySettingsApp = loadable(() =>
  import(
    /* webpackChunkName: "PrivacySettingsApp" */ './containers/Privacy/Settings/SettingsApp'
  )
)

const PreClassificationPayloadApp = loadable(() =>
  import(
    /* webpackChunkName: "PreClassificationPayloadApp" */ './containers/Privacy/PreClassificationPayload'
  )
)

const TrackingPlanLiveEditorApp = loadable(() =>
  import(
    /* webpackChunkName: "TrackingPlanEditorApp" */ './containers/Protocols/TrackingPlanApp/EditorApp'
  )
)
const TrackingPlanReadOnlyApp = loadable(() =>
  import(
    /* webpackChunkName: "TrackingPlanReadOnlyApp" */ './containers/Protocols/TrackingPlanApp/ReadOnlyApp'
  )
)
const TrackingPlanMergeChangesApp = loadable(() =>
  import(
    /* webpackChunkName: "TrackingPlaMergeChangesApp" */ './containers/Protocols/TrackingPlanApp/MergeChangesApp'
  )
)
const DataValidationApp = loadable(() =>
  import(
    /* webpackChunkName: "DataValidationApp" */ './containers/Protocols/TrackingPlanApp/DataValidationApp'
  )
)
const TrackingPlanListApp = loadable(() =>
  import(
    /* webpackChunkName: "TrackingPlanListApp" */ './containers/Protocols/TrackingPlanApp/ListApp'
  )
)

const TrackingPlanConnectedSourcesSidesheetApp = loadable(() =>
  import(
    /* webpackChunkName: "TrackingPlanConnectedSourcesSidesheetApp" */ './containers/Protocols/TrackingPlanApp/ConnectedSourcesSidesheetApp/ConnectedSourcesSidesheetApp'
  )
)

const TrackingPlanConnectedLibrariesSidesheet = loadable(() =>
  import(
    /* webpackChunkName: "TrackingPlanConnectedLibrariesSidesheet" */ './components/Protocols/TrackingPlan/LibraryTrackingPlanConnections/ConnectedLibrariesSidesheet.tsx'
  )
)

const LibraryConnectedTrackingPlansSidesheet = loadable(() =>
  import(
    /* webpackChunkName: "LibraryConnectedTrackingPlansSidesheet" */ './components/Protocols/TrackingPlan/LibraryTrackingPlanConnections/ConnectedTrackingPlansSidesheet.tsx'
  )
)

const TrackingPlanImportLibrariesDialogApp = loadable(() =>
  import(
    /* webpackChunkName: "TrackingPlanConnectedSourcesSidesheetApp" */ './containers/Protocols/ImportLibrariesApp/ImportLibrariesApp'
  )
)

const SummariesPerSourceApp = loadable(() =>
  import(
    /* webpackChunkName: "SummariesPerSourceApp" */ './containers/Protocols/Violations/SummariesPerSourceApp'
  )
)
const LibrariesApp = loadable(() =>
  import(
    /* webpackChunkName: "LibrariesApp" */ './containers/Protocols/LibrariesApp'
  )
)
const ChangeLogApp = loadable(() =>
  import(
    /* webpackChunkName: "ChangeLogApp" */ './containers/Protocols/TrackingPlanApp/ChangeLogApp'
  )
)
const ConnectSourceApp = loadable(() =>
  import(
    /* webpackChunkName: "ConnectSourceApp" */ './containers/Protocols/TrackingPlanApp/ConnectSourceApp'
  )
)
const TransformationsOverview = loadable(() =>
  import(
    /* webpackChunkName: "TransformationsOverview" */ './containers/Protocols/TransformationsOverview'
  )
)
const TransformationsWizardApp = loadable(() =>
  import(
    /* webpackChunkName: "TransformationsWizardApp" */ './containers/Protocols/TransformationsWizard/TransformationsWizardApp'
  )
)
const SourceSchemaEvents = loadable(() =>
  import(
    /* webpackChunkName: "SourceSchemaEvents" */ './containers/Sources/SourceSchema/EventsApp'
  )
)
const SourceSchemaIdentifyGroup = loadable(() =>
  import(
    /* webpackChunkName: "SourceSchemaIdentifyGroup" */ './containers/Sources/SourceSchema/IdentifyGroupApp'
  )
)
const SourceSettingsApp = loadable(() =>
  import(
    /* webpackChunkName: "SourceSettingsApp" */ './containers/Sources/Settings/App'
  )
)
const SourceSettingsCrossDomainAnalyticsApp = loadable(() =>
  import(
    /* webpackChunkName: "SourceSettingsCrossDomainAnalyticsApp" */ './containers/Sources/Settings/CrossDomainAnalyticsApp'
  )
)
const SourceSettingsCollaboratorsApp = loadable(() =>
  import(
    /* webpackChunkName: "SourceSettingsCollaboratorsApp" */ './containers/Sources/Settings/CollaboratorsApp'
  )
)
const SourceSettingsFacebookAdsApp = loadable(() =>
  import(
    /* webpackChunkName: "SourceSettingsFacebookAdsApp" */ './containers/Sources/Settings/FacebookAdsApp'
  )
)
const SourceSettingsConnectionApp = loadable(() =>
  import(
    /* webpackChunkName: "SourceSettingsConnectionApp" */ './containers/Sources/Settings/ConnectionApp'
  )
)
const SourceSettingsEndpointApp = loadable(() =>
  import(
    /* webpackChunkName: "SourceSettingsEndpointApp" */ './components/Sources/Settings/Endpoint'
  )
)
const SourceSettingsSchemaConfigApp = loadable(() =>
  import(
    /* webpackChunkName: "SourceSettingsSchemaConfigApp" */ './containers/Sources/Settings/SchemaConfigApp'
  )
)
const SourceSettingsRefreshSchemaApp = loadable(() =>
  import(
    /* webpackChunkName: "SourceSettingsRefreshSchemaApp" */ './containers/Sources/Settings/RefreshSchemaApp'
  )
)
const SourceSettingsBasicApp = loadable(() =>
  import(
    /* webpackChunkName: "SourceSettingsBasicApp" */ './containers/Sources/Settings/BasicApp'
  )
)
const SourceSettingsMoveApp = loadable(() =>
  import(
    /* webpackChunkName: "SourceSettingsMoveApp" */ './containers/Sources/Settings/MoveApp'
  )
)
const SourceSettingsKeysApp = loadable(() =>
  import(
    /* webpackChunkName: "SourceSettingsKeysApp" */ './containers/Sources/Settings/KeysApp'
  )
)
const SourcePluginsApp = loadable(() =>
  import(
    /* webpackChunkName: "SourcePluginsApp" */ './containers/Sources/Settings/PluginsApp'
  )
)
const SourceSettingsAnalyticsJsApp = loadable(() =>
  import(
    /* webpackChunkName: "SourceAnalyticsJsApp" */ './containers/Sources/Settings/AnalyticsJsApp'
  )
)
const SourceSettingsSqlApp = loadable(() =>
  import(
    /* webpackChunkName: "SourceSettingsSqlApp" */ './containers/Sources/Settings/SqlApp'
  )
)
const SourceSettingsSelectiveSyncApp = loadable(() =>
  import(
    /* webpackChunkName: "SourceSettingsSelectiveSyncApp" */ './containers/Sources/Settings/SelectiveSyncApp'
  )
)
const SourceSchemaEventProperties = loadable(() =>
  import(
    /* webpackChunkName: "SourceSchemaEventProperties" */ './containers/Sources/SourceSchema/EventPropertiesApp'
  )
)
const WarehouseApp = loadable(() =>
  import(
    /* webpackChunkName: "WarehouseApp" */ './containers/Warehouses/Warehouse'
  )
)
const WarehouseOverview = loadable(() =>
  import(
    /* webpackChunkName: "WarehouseOverview" */ './containers/Warehouses/Overview'
  )
)
const WarehouseRunSheet = loadable(() =>
  import(
    /* webpackChunkName: "WarehouseRunSheet" */ './containers/Warehouses/RunSheet'
  )
)
const WarehouseSettingsConnection = loadable(() =>
  import(
    /* webpackChunkName: "WarehouseSettingsConnection" */ './containers/Warehouses/SettingsConnection'
  )
)
const WarehouseSettingsIndex = loadable(() =>
  import(
    /* webpackChunkName: "WarehouseSettingsIndex" */ './containers/Warehouses/Settings'
  )
)
const WarehouseSettingsMaintenance = loadable(() =>
  import(
    /* webpackChunkName: "WarehouseSettingsMaintenance" */ './containers/Warehouses/SettingsMaintenance'
  )
)
const WarehouseSettingsSelectiveSync = loadable(() =>
  import(
    /* webpackChunkName: "WarehouseSettingsSelectiveSync" */ './containers/Warehouses/SettingsSelectiveSync'
  )
)
const WarehouseSettingsSyncSchedule = loadable(() =>
  import(
    /* webpackChunkName: "WarehouseSettingsSyncSchedule" */ './containers/Warehouses/SettingsSyncSchedule'
  )
)
const WarehouseSetupApp = loadable(() =>
  import(
    /* webpackChunkName: "WarehouseSetupApp" */ './containers/Warehouses/SetupApp'
  )
)
const WarehouseSourceApp = loadable(() =>
  import(
    /* webpackChunkName: "WarehouseSourceApp" */ './containers/Warehouses/WarehouseSource'
  )
)
const WarehouseSourceSyncHistory = loadable(() =>
  import(
    /* webpackChunkName: "WarehouseSourceSyncHistory" */ './containers/Warehouses/WarehouseSource/SyncHistory'
  )
)
const WarehouseSourceSettings = loadable(() =>
  import(
    /* webpackChunkName: "WarehouseSourceSettings" */ './containers/Warehouses/WarehouseSource/Settings'
  )
)
const PartnersApp = loadable(() =>
  import(/* webpackChunkName: "PartnersApp" */ './containers/Partners/App')
)
const PartnersCreateApp = loadable(() =>
  import(
    /* webpackChunkName: "PartnersCreateApp" */ './containers/Partners/CreateApp'
  )
)
const PartnerApp = loadable(() =>
  import(
    /* webpackChunkName: "PartnerApp" */ './containers/Partners/Partner/App'
  )
)
const PartnerNoteApp = loadable(() =>
  import(
    /* webpackChunkName: "PartnerNoteApp" */ './containers/Partners/Partner/NoteApp'
  )
)
const PartnerJsonApp = loadable(() =>
  import(
    /* webpackChunkName: "PartnerJsonApp" */ './containers/Partners/Partner/JsonApp'
  )
)
const PartnerLogosApp = loadable(() =>
  import(
    /* webpackChunkName: "PartnerLogosApp" */ './containers/Partners/Partner/LogosApp'
  )
)
const PartnerAnalyticsApp = loadable(() =>
  import(
    /* webpackChunkName: "PartnerAnalyticsApp" */ './containers/Partners/Partner/AnalyticsApp'
  )
)
const PartnerProfileApp = loadable(() =>
  import(
    /* webpackChunkName: "PartnerProfileApp" */ './containers/Partners/Partner/ProfileApp'
  )
)
const PartnerMethodsApp = loadable(() =>
  import(
    /* webpackChunkName: "PartnerMethodsApp" */ './containers/Partners/Partner/MethodsApp'
  )
)
const PartnerPlatformsApp = loadable(() =>
  import(
    /* webpackChunkName: "PartnerPlatformsApp" */ './containers/Partners/Partner/PlatformsApp'
  )
)
const PartnerCategoriesApp = loadable(() =>
  import(
    /* webpackChunkName: "PartnerCategoriesApp" */ './containers/Partners/Partner/CategoriesApp'
  )
)
const PartnerReplayApp = loadable(() =>
  import(
    /* webpackChunkName: "PartnerReplayApp" */ './containers/Partners/Partner/ReplayApp'
  )
)
const PartnersReportApp = loadable(() =>
  import(
    /* webpackChunkName: "PartnersReportApp" */ './containers/Partners/Report/App'
  )
)
const PartnerDirectIntegrationApp = loadable(() =>
  import(
    /* webpackChunkName: "PartnerDirectIntegrationApp" */ './containers/Partners/Partner/DirectIntegrationApp'
  )
)
const PartnerComponentsApp = loadable(() =>
  import(
    /* webpackChunkName: "PartnerComponentsApp" */ './containers/Partners/Partner/ComponentsApp'
  )
)
const PartnerContactsApp = loadable(() =>
  import(
    /* webpackChunkName: "PartnerContactsApp" */ './containers/Partners/Partner/ContactsApp'
  )
)
const PartnerConnectionModeApp = loadable(() =>
  import(
    /* webpackChunkName: "PartnerConnectionModeApp" */ './containers/Partners/Partner/ConnectionModeApp'
  )
)
const PartnerSettingsApp = loadable(() =>
  import(
    /* webpackChunkName: "PartnerSettingsApp" */ './containers/Partners/Partner/SettingsApp'
  )
)
const PartnerStatusApp = loadable(() =>
  import(
    /* webpackChunkName: "PartnerStatusApp" */ './containers/Partners/Partner/StatusApp'
  )
)
const ConnectionHealthApp = loadable(() =>
  import(
    /* webpackChunkName: "ConnectionHealth" */ './containers/ConnectionHealth'
  )
)
const UniversalWorkspace = loadable(() =>
  import(
    /* webpackChunkName: "UniversalWorkspace" */ './containers/UniversalWorkspace'
  )
)

const HomeApp = loadable(() =>
  import(/* webpackChunkName: "HomeApp" */ './containers/HomeApp')
)

const InsightsApp = loadable(() =>
  import(
    /* webpackChunkName: "InsightsApp" */ './containers/WorkspaceSettings/Insights'
  )
)

const NewUsage = loadable(() =>
  import(
    /* webpackChunkName: "NewUsage" */ './containers/WorkspaceSettings/NewUsage'
  )
)

const VisualTaggerV2 = loadable(() =>
  import(
    /* webpackChunkName: "VisualTaggerV2" */ './containers/VisualTagger/indexV2'
  )
)

const VisualTaggerHome = loadable(() =>
  import(
    /* webpackChunkName: "VisualTaggerHome" */ './containers/VisualTagger/components/Home/HomeApp'
  )
)

const VisualTaggerPrereqs = loadable(() =>
  import(
    /* webpackChunkName: "VisualTaggerPrereqs" */ './containers/VisualTagger/components/Prerequisites/PrerequisitesApp'
  )
)

const VisualTaggerGuide = loadable(() =>
  import(
    /* webpackChunkName: "VisualTaggerGuide" */ './containers/VisualTagger/components/Guide/GuideApp'
  )
)

const VisualTaggerCreateTags = loadable(() =>
  import(
    /* webpackChunkName: "VisualTaggerCreateTags" */ './containers/VisualTagger/components/CreateTags/CreateTagsApp'
  )
)

const VisualTaggerEnterURL = loadable(() =>
  import(
    /* webpackChunkName: "VisualTaggerEnterURL" */ './containers/VisualTagger/components/EnterURL/EnterURLApp'
  )
)

const CommandCenterApp = loadable(() =>
  import(
    /* webpackChunkName: "CommandCenterApp" */ './containers/CommandCenter'
  )
)

function withCanonicalUrls(children, pathname) {
  return (
    <Fragment>
      <Helmet>
        <link rel="canonical" href={`${APP_URL}${pathname}`} />
        <meta property="og:image" content={OgImage} />
      </Helmet>
      {children}
    </Fragment>
  )
}

// Store last path to determine in `renderRoute()` if path actually changed,
// or it's some other change like new/changed query string params
let lastPathname

function renderRoute(props) {
  /*
    Force a full page reload if app is not on the latest version.
    This will guarantee customers are always on the most recent
    version of the app the moment they decide to navigate away from
    their current path.
  */
  const { pathname, search } = props.location
  const isPathChanged = pathname !== lastPathname

  if (isPathChanged && !isOnLatest()) {
    window.location = `${pathname}${search}`
    return null
  }

  lastPathname = pathname

  return withCanonicalUrls(
    // TODO: fix this lint issue with the useScroll usage below
    // eslint-disable-next-line react-hooks/rules-of-hooks
    applyRouterMiddleware(useScroll(scrollBehavior))(props),
    pathname
  )
}

const PERSONAS = 'personas'
const GROWTH = 'growth'
const PLATFORM_CX = 'platform-cx'
const FOUNDATION_CORE = 'foundation-core'
const PROTOCOLS = 'protocols'
const TOOLING = 'tooling'
const ENTERPRISE_CORE = 'enterprise-core'
const ROUTING = 'ROUTING'
const SOURCES = 'SOURCES'
const FUNCTIONS = 'FUNCTIONS'

function shouldAutoTrack(routes) {
  const lastRoute = Array.from(routes).reverse()[0]
  return lastRoute && !lastRoute.disableAutoPageTracking
}

function getRoutePageName(routes) {
  return Array.from(routes).reverse()[0].pageName
}

function trackPageView({ routes, params }) {
  // Note: we have legacy ad-hoc `analytics.page` calls throughout the codebase we need to respect
  const route = routes.map(r => r.path).join('/')
  const name = getRoutePageName(routes)
  const props = {
    autotracked: true,
    version: 2,
    route_params: params,
    route
  }

  if (params.workspaceSlug) {
    props.workspace_slug = params.workspaceSlug
  }

  if (params.sourceSlug) {
    props.source_slug = params.sourceSlug
  }

  if (name) {
    window.analytics.page(name, props)
  } else {
    window.analytics.page(props)
  }
}

function onRouteChange() {
  /*
    React Router binds this function for us
    That's why we have the named old-school function definition here 😅
  */

  RouteTracker.setRoute(this.router)

  // Track page views as the route changes
  if (shouldAutoTrack(this.router.routes)) {
    trackPageView(this.router)
  }
}

const routes = () => (
  <Router
    onUpdate={onRouteChange}
    history={browserHistory}
    render={renderRoute}
    createElement={(Component, props) => {
      // These checks should be useless, but this function is responsible for
      // rendering every single page in the app and I really don't want to break it (again)
      try {
        if (typeof props === 'object' && Array.isArray(props.routes)) {
          // Find current route, it's always last in the `routes` chain
          const currentRoute = props.routes[props.routes.length - 1]

          // If <Route> has `preload` prop, preload these components for faster navigation
          if (
            typeof currentRoute === 'object' &&
            Array.isArray(currentRoute.preload)
          ) {
            for (const nextRoute of currentRoute.preload) {
              if (
                typeof nextRoute === 'object' &&
                typeof nextRoute.preload === 'function'
              ) {
                nextRoute.preload()
              }
            }
          }
        }
      } catch (error) {
        trackError(error)
      }

      return (
        <RouterProvider>
          <Component {...props} />
        </RouterProvider>
      )
    }}
  >
    <Redirect from="/" to="/workspaces" />

    <Route
      exact
      path="/settings/user/mfa/check"
      component={MfaSecurityCheckApp}
      pageName="MFA security check"
      team={GROWTH}
    />
    <Route
      path="/settings/user"
      component={UserSettingsApp}
      team={GROWTH}
      pageName="User Settings"
    >
      <Route
        path="change-password"
        component={ChangePasswordApp}
        team={GROWTH}
        pageName="Change Password"
      />
      <Route
        path="mfa"
        component={MfaApp}
        team={ENTERPRISE_CORE}
        pageName="Multi-Factor Authentication"
      />
    </Route>

    <Route path="/login" pageName="Login" component={LoginApp} team={GROWTH} />
    <Route path="/logout" pageName="Logout" component={Logout} team={GROWTH} />
    <Route
      path="/verify"
      pageName="verify"
      component={VerifyApp}
      team={GROWTH}
    />
    <Route
      path="/signup"
      disableAutoPageTracking
      component={SignupApp}
      team={GROWTH}
    />
    <Route
      path="/startup-program"
      disableAutoPageTracking
      component={StartupSignup}
      team={GROWTH}
    />
    <Route
      path="/forgot"
      pageCategory="Password"
      pageName="Forgot"
      component={ForgotApp}
      team={GROWTH}
    />
    <Route
      path="/reset"
      pageCategory="Password"
      pageName="Reset"
      component={ResetApp}
      team={GROWTH}
    />
    <Route
      path="/forced-reset"
      pageCategory="Password"
      pageName="Forced Reset"
      component={ForcedResetApp}
      team={ENTERPRISE_CORE}
    />
    <Route
      path="/enable"
      pageName="Enable with Segment"
      component={EnableApp}
      team={PLATFORM_CX}
    />
    <Route path="/authorize" component={AuthorizeApp} team={PLATFORM_CX} />
    <Route
      path="/workspaces"
      pageName="Workspace List View"
      component={WorkspacesHome}
      team={GROWTH}
    >
      <Route path="new" component={WorkspacesNew} />
    </Route>

    <Route
      path="/partner-portal/report"
      pageName="Partner Portal Report"
      component={PartnersReportApp}
      team={PLATFORM_CX}
    />
    <Redirect from="/partner-portal" to="/partner-portal/integration" />
    <Route
      path="/partner-portal/:metadataType"
      component={PartnersApp}
      team={PLATFORM_CX}
    >
      <Route path="create" component={PartnersCreateApp} />
      <Route path=":slug" component={PartnerApp}>
        <IndexRedirect to="profile" />
        <Route
          path="profile"
          pageName="Partner Portal Profile"
          component={PartnerProfileApp}
        />
        <Route
          path="status"
          pageName="Partner Portal Status"
          component={PartnerStatusApp}
        />
        <Route
          path="metrics"
          pageName="Partner Portal Metrics"
          component={PartnerAnalyticsApp}
        />
        <Route
          path="logos"
          pageName="Partner Portal Logos"
          component={PartnerLogosApp}
        />
        <Route
          path="json"
          pageName="Partner Portal Raw Profile"
          component={PartnerJsonApp}
        />
        <Route
          path="note"
          pageName="Partner Portal Methods"
          component={PartnerNoteApp}
        />
        <Route
          path="methods"
          pageName="Partner Portal Methods"
          component={PartnerMethodsApp}
        />
        <Route
          path="platforms"
          pageName="Partner Portal Platforms"
          component={PartnerPlatformsApp}
        />
        <Route
          path="categories"
          pageName="Partner Portal Categories"
          component={PartnerCategoriesApp}
        />
        <Route
          path="replay"
          pageName="Partner Portal Replay Policy"
          component={PartnerReplayApp}
        />
        <Route
          path="direct-integration"
          pageName="Partner Portal Direct Integration'"
          component={PartnerDirectIntegrationApp}
        />
        <Route
          path="components"
          pageName="Partner Portal Components"
          component={PartnerComponentsApp}
        />
        <Route
          path="contacts"
          pageName="Partner Portal Contacts"
          component={PartnerContactsApp}
        />
        <Route
          path="settings"
          pageName="Partner Portal Settings"
          component={PartnerSettingsApp}
        />
        <Route
          path="connection-mode"
          pageName="Partner Portal Connection Mode"
          component={PartnerConnectionModeApp}
        />
      </Route>
    </Route>

    <Route
      path="/goto-my-workspace/*"
      component={UniversalWorkspace}
      team={PLATFORM_CX}
    />

    <Route path="/:workspaceSlug" component={WorkspaceApp} team={GROWTH}>
      <IndexRedirect to="redirect" />
      <Route
        path="welcome"
        pageName="Welcome"
        component={WelcomeApp}
        disableGlobalTopNav
        disableGlobalSideNav
      />

      <Route
        path="command-center"
        pageName="Command Center"
        component={CommandCenterApp}
        team="coolest team in the building"
        disableGlobalTopNav
        disableGlobalSideNav
      />

      <Route
        path="setup"
        disableAutoPageTracking
        component={SetupApp}
        disableGlobalTopNav
        disableGlobalSideNav
      />

      <Route
        path="home"
        pageName="Workspace Home"
        label="Home"
        component={HomeApp}
      />

      <Route
        path="upgrade"
        pageName="Upgrade Flow"
        component={UpgradeFlowApp}
        disableGlobalTopNav
        disableGlobalSideNav
      />

      <Route path="redirect" components={WorkspaceRedirect} />

      <Route
        path="quickstart(/:sourceSlug)"
        component={WorkspaceQuickStart}
        disableGlobalTopNav
        disableGlobalSideNav
      >
        <Route
          path="destinations/auto-detect"
          pageName="One Click Destinations - Auto Detect"
          component={WorkspaceQuickStartAutoDetect}
        />

        <Route
          path="destinations(/:destinationSlug)"
          component={WorkspaceQuickStartDestinationSelection}
        />

        <Route
          path="destinations/:destinationSlug/config"
          component={WorkspaceQuickStartDestinationConfig}
        />
        <Route
          path="destinations/:destinationSlug/install"
          component={WorkspaceQuickStartSourceInstall}
        />
      </Route>

      <Route path="settings" component={WorkspaceSettings}>
        <IndexRedirect to="basic" />
        <Route
          path="basic"
          pageName="Workspace Settings Basic"
          components={WorkspaceSettingsBasicApp}
          label="Settings"
        >
          <Route
            path="delete"
            components={WorkspaceSettingsDeletionApp}
            label="Settings"
          />
        </Route>

        <Redirect from="billing" to="usage" />
        <Redirect from="api-usage" to="usage" />
        <Route
          path="usage"
          components={WorkspaceSettingsUsageApp}
          label="Settings"
        />

        <Route
          path="advanced-usage"
          components={WorkspaceSettingsAdvancedUsageApp}
          label="Settings"
        />

        <Route
          path="insights"
          component={InsightsApp}
          label="Settings"
          pageName="Insights"
          team={GROWTH}
        />

        <Route
          path="new-usage"
          component={NewUsage}
          label="Settings"
          pageName="Usage"
          team={GROWTH}
        />

        <Redirect from="activity-feed" to="audit-settings" />
        <Route
          path="audit-settings"
          pageName="Workspace Settings Audit Settings"
          components={WorkspaceSettingsAuditForwardingApp}
          label="Settings"
          team={ENTERPRISE_CORE}
        />
        <Route
          path="support-access"
          pageName="Workspace Settings Support Access"
          components={WorkspaceSettingsSupportAccessApp}
          label="Settings"
        />
        <Redirect from="team" to="access-management" />
        <Route
          path="access-management"
          components={WorkspaceSettingsAccessManagementApp}
          label="Settings"
          team={ENTERPRISE_CORE}
        >
          <Route
            path="invite"
            hideSidebar
            components={WorkspaceSettingsAccessManagementApp}
            team={ENTERPRISE_CORE}
          />
        </Route>
        <Route
          path="groups"
          component={WorkspaceSettingsGroupsPermissions}
          label="Settings"
          team={ENTERPRISE_CORE}
        />
        <Route
          path="groups/new"
          hideSidebar
          component={WorkspaceSettingsCreateGroup}
          team={ENTERPRISE_CORE}
        />
        <Route
          path="groups/new/users"
          hideSidebar
          component={WorkspaceSettingsGroupSetupUsers}
          team={ENTERPRISE_CORE}
        />
        <Route
          path="sources-labels"
          pageName="Source Labels"
          components={WorkspaceSettingsSourcesLabelsApp}
          team={ENTERPRISE_CORE}
        />
        <Route
          path="label-management"
          components={WorkspaceSettingsLabelManagementApp}
          team={ENTERPRISE_CORE}
        />
        <Route
          path="sources-environments"
          pageName="Source Environment bulk assignment"
          components={WorkspaceSettingsSourcesEnvironmentsApp}
          label="Settings"
          team={ENTERPRISE_CORE}
        />

        <Redirect from="newnotifications" to="notifications" />
        <Route
          path="notifications"
          pageName="Workspace Settings Notifications"
          components={WorkspaceSettingsNotificationsV2App}
          label="Settings"
        >
          <Route
            path="delivery-alerts"
            pageName="Workspace Settings Delivery Alert Subscriptions"
            label="Settings"
            components={IntegrationHealthNotificationsSidesheet}
          />
        </Route>

        <Route
          path="connections"
          pageName="Workspace Settings Authentication"
          components={WorkspaceSettingsConnectionsApp}
          label="Settings"
          team={ENTERPRISE_CORE}
        />
        <Route
          path="edit-connection/:connectionId"
          components={WorkspaceSettingsConnectionEditApp}
          label="Settings"
          team={ENTERPRISE_CORE}
        />
        <Route
          path="setup-connection"
          components={WorkspaceSettingsConnectionSetupApp}
          team={ENTERPRISE_CORE}
        >
          <IndexRedirect to="select" />
          <Route
            path="select"
            components={WorkspaceSettingsConnectionSetupSelectApp}
            label="Settings"
          />

          <Route
            path="configure-connection"
            components={WorkspaceSettingsConnectionSetupConnectionApp}
            label="Settings"
          />

          <Route
            path="configure-idp"
            components={WorkspaceSettingsConnectionSetupIDPApp}
            label="Settings"
          />
          <Route
            path="configure-domains"
            pageName="Workspace Settings Domains"
            components={WorkspaceSettingsDomainsApp}
            label="Settings"
          />

          <Route
            path="complete"
            components={WorkspaceSettingsConnectionSetupCompleteApp}
            label="Settings"
          />
        </Route>
        <Route
          path="domains"
          components={WorkspaceSettingsDomainsApp}
          label="Settings"
          team={ENTERPRISE_CORE}
        />
        <Route
          path="advanced"
          components={WorkspaceSettingsAdvancedApp}
          label="Settings"
          team={ENTERPRISE_CORE}
        />
        <Route
          path="plans"
          pageName="Workspace Settings Plans"
          components={WorkspaceSettingsPlansApp}
          label="Settings"
        >
          <Route
            path="downgrade"
            components={WorkspaceSettingsDowngradeApp}
            label="Settings"
          />
        </Route>
        <Route
          path="card"
          components={WorkspaceSettingsCardApp}
          label="Settings"
        />

        <Route
          path="payments"
          pageName="Workspace Settings Payments"
          components={WorkspaceSettingsPaymentsApp}
          label="Settings"
        />

        <Redirect
          path="change-plan/developer/*"
          to="change-plan/team-monthly"
        />
        <Route
          path="change-plan/:planId"
          components={WorkspaceSettingsPlanChangeApp}
          label="Settings"
        >
          <IndexRedirect to="select" />
          <Route
            path="select"
            components={WorkspaceSettingsPlanChangeSelectApp}
            ignoreScrollBehavior
            label="Settings"
          />

          <Route
            path="confirm"
            components={WorkspaceSettingsPlanChangeConfirmApp}
            label="Settings"
          />

          <Route
            path="thanks"
            components={WorkspaceSettingsPlanChangeThanksApp}
          />
        </Route>

        <Route
          path="privacy-security"
          components={WorkspaceSettingsPrivacySecurityApp}
          label="Settings"
        />

        <Route
          path="suppressed-users"
          components={WorkspaceSettingsSuppressedUsersApp}
          label="Settings"
          team={FOUNDATION_CORE}
        />

        <Route
          path="deletion-requests"
          components={WorkspaceSettingsDeletionRequestsApp}
          label="Settings"
          team={FOUNDATION_CORE}
        />

        <Route
          path="audit-trail"
          components={WorkspaceSettingsAuditTrailApp}
          label="Settings"
          team={ENTERPRISE_CORE}
        />

        <Route
          path="data-retention"
          components={WorkspaceSettingsDataRetentionApp}
          pageName="Data Retention"
          label="Settings"
          team={FOUNDATION_CORE}
        />

        <Route
          path="spaces"
          component={WorkspaceSettingsSpacesListApp}
          pageName="Spaces"
          label="Settings"
          team={PERSONAS}
        />

        <Route
          path="spaces/new"
          component={WorkspaceSettingsSpacesCreateApp}
          pageName="Create Space"
          label="Settings"
          team={PERSONAS}
        />
      </Route>

      <Route
        path="privacy"
        label="Privacy"
        component={PrivacyApp}
        team={PROTOCOLS}
      >
        <Route path="welcome" component={PrivacyWelcomeApp} />
        <Route path="consent" isFullWidth>
          <IndexRedirect to="1" />
          <Route path=":step" component={PrivacyConsentApp} />
        </Route>
        <Route path="inventory" component={PrivacyInventoryApp} isFullWidth>
          <Route path=":fieldName">
            <IndexRedirect to="overview" />
            <Route
              path=":subsection"
              component={PrivacyInventorySidesheetApp}
            />
          </Route>
        </Route>
        <Route
          path="detection"
          label="Privacy"
          component={PrivacyDictionaryApp}
          isFullWidth
        />
        <Redirect from="dictionary" to="detection" />
        <Route path="catalog" label="Privacy" component={PrivacyCatalogApp} />
        <Route
          path="settings"
          label="Privacy"
          component={PrivacySettingsApp}
          isFullWidth
        >
          <IndexRoute component={PrivacyReviewApp} />
          <Route
            group="Advanced Configuration"
            title="Alerts"
            path="alerts"
            component={PrivacyReviewApp}
          />
          <Route
            group="Advanced Configuration"
            title="Standard Controls"
            path="controls"
            component={PrivacyControlsApp}
          />

          <Route
            group="Advanced Configuration"
            title="Advanced Controls"
            path="advanced-controls"
            isPremium
            component={PrivacyAdvancedControlsApp}
          />
          <Route
            path="security"
            title="Privacy & Security"
            group="Access"
            component={WorkspaceSettingsPrivacySecurityApp}
          />
          <Route
            path="deletion-requests"
            title="Deletion Requests"
            group="Access"
            component={WorkspaceSettingsDeletionRequestsApp}
          />
          <Route
            path="data-retention"
            title="Data Retention"
            group="Access"
            component={WorkspaceSettingsDataRetentionApp}
          />
          <Route
            path="suppressed-users"
            title="Suppressed Users"
            group="Access"
            component={WorkspaceSettingsSuppressedUsersApp}
          />
        </Route>
        <Route path="inbox" component={PrivacyInboxApp} isFullWidth>
          <Route
            path="payload/:preClassificationId"
            component={PreClassificationPayloadApp}
          />
        </Route>
      </Route>

      <Route path="protocols" component={ProtocolsRootApp} team={PROTOCOLS}>
        <IndexRedirect to="tracking-plans" />
        <Route
          path="violations"
          component={SummariesPerSourceApp}
          label="Protocols"
          disableProtocolsFeedbackBanner
        />
        <Route
          path="violations/:sourceSlug"
          disableAutoPageTracking
          label="Protocols"
          disableProtocolsFeedbackBanner
        >
          <IndexRedirect to="track" />
          <Route
            path=":type"
            disableAutoPageTracking
            label="Protocols"
            component={SummariesPerEventApp}
          >
            <Route
              path=":name"
              disableAutoPageTracking
              label="Protocols"
              component={SummariesPerViolationApp}
            />
          </Route>
        </Route>
        <Route
          path="transformations"
          component={TransformationsOverview}
          label="Protocols"
        >
          <Route
            path="create"
            component={TransformationsWizardApp}
            label="Protocols"
            disableProtocolsFeedbackBanner
          />
          <Route
            path=":transformationId/edit"
            component={TransformationsWizardApp}
            label="Protocols"
            disableProtocolsFeedbackBanner
          />
        </Route>
        <Route
          path="libraries"
          component={LibrariesApp}
          label="Protocols"
          disableProtocolsFeedbackBanner
        >
          <Route
            path=":trackingPlanId"
            disableAutoPageTracking
            component={TrackingPlanReadOnlyApp}
          >
            <Route
              path="synced-tracking-plans"
              disableAutoPageTracking
              component={LibraryConnectedTrackingPlansSidesheet}
            />
            <Route
              path="change-log"
              disableAutoPageTracking
              component={ChangeLogApp}
            />
          </Route>
          <Route
            path=":trackingPlanId/draft"
            disableAutoPageTracking
            component={TrackingPlanLiveEditorApp}
          >
            <Route
              path="change-log"
              disableAutoPageTracking
              component={ChangeLogApp}
            />
            <Route
              path="synced-tracking-plans"
              disableAutoPageTracking
              component={LibraryConnectedTrackingPlansSidesheet}
            />
          </Route>
          <Route
            path=":trackingPlanId/merge"
            disableAutoPageTracking
            component={TrackingPlanMergeChangesApp}
          >
            <Route
              path="synced-tracking-plans"
              disableAutoPageTracking
              component={LibraryConnectedTrackingPlansSidesheet}
            />
          </Route>
          <Route
            path=":trackingPlanId/list-synced-tracking-plans"
            disableAutoPageTracking
            component={LibraryConnectedTrackingPlansSidesheet}
          />
        </Route>
        <Route
          path="data-validation/:trackingPlanId"
          disableAutoPageTracking
          disableGlobalTopNav
          disableProtocolsTopNav
          component={DataValidationApp}
        >
          <Route
            path="sources/:sourceSlug/:type/:name"
            disableAutoPageTracking
            component={SummariesPerViolationApp}
          />
        </Route>
        <Route
          path="tracking-plans"
          label="Protocols"
          component={TrackingPlanListApp}
          disableProtocolsFeedbackBanner
        >
          <Route
            path=":trackingPlanId/draft"
            disableAutoPageTracking
            component={TrackingPlanLiveEditorApp}
          >
            <Route
              path="change-log"
              disableAutoPageTracking
              component={ChangeLogApp}
            />
            <Route
              path="import/:trackingPlanType"
              disableAutoPageTracking
              component={TrackingPlanImportLibrariesDialogApp}
            />
            <Route
              path="synced-libraries"
              disableAutoPageTracking
              component={TrackingPlanConnectedLibrariesSidesheet}
            />
          </Route>
          <Route
            path=":trackingPlanId"
            disableAutoPageTracking
            component={TrackingPlanReadOnlyApp}
          >
            <Route
              path="change-log"
              disableAutoPageTracking
              component={ChangeLogApp}
            />
            <Route
              path="synced-libraries"
              disableAutoPageTracking
              component={TrackingPlanConnectedLibrariesSidesheet}
            />
          </Route>
          <Route
            path=":trackingPlanId/merge"
            disableAutoPageTracking
            component={TrackingPlanMergeChangesApp}
          />
          <Route
            path="connect/:trackingPlanId"
            disableAutoPageTracking
            component={ConnectSourceApp}
          />
          <Route
            path=":trackingPlanId/sources"
            disableAutoPageTracking
            component={TrackingPlanConnectedSourcesSidesheetApp}
          />
          <Route
            path=":trackingPlanId/list-synced-libraries"
            disableAutoPageTracking
            component={TrackingPlanConnectedLibrariesSidesheet}
          />
        </Route>
      </Route>
      <Route
        path="overview"
        pageName="Workspace Overview"
        component={WorkspaceOverviewApp}
        label="Overview"
      />
      <Route
        path="overview-v2"
        pageName="Workspace Overview v2"
        component={WorkspaceOverviewAppV2}
        label="Overview"
      />
      <Redirect from="integrations/catalog" to="destinations/catalog" />
      <Redirect from="warehouses" to="destinations" />
      <Route
        path="destinations"
        pageName="Workspace Destinations"
        component={WorkspaceDestinationsApp}
        label="List of Destinations"
        team={PLATFORM_CX}
      />

      <Route
        path="destinations/catalog"
        pageName="Integrations Catalog"
        label="Catalog"
        component={DestinationsCatalogApp}
        breadcrumb={{
          isCatalog: true
        }}
        ignoreScrollBehavior
        team={PLATFORM_CX}
      />
      <Route
        path="destinations/catalog/new"
        pageName="Integrations Catalog"
        label="Catalog"
        component={DestinationsCatalogApp}
        breadcrumb={{
          isCatalog: true
        }}
        ignoreScrollBehavior
        team={PLATFORM_CX}
      />
      <Route
        path="build/catalog"
        pageName="Build"
        disableAutoPageTracking
        label="Catalog"
        component={BuildCatalogApp}
        breadcrumb={{
          isCatalog: true
        }}
        ignoreScrollBehavior
        team={PLATFORM_CX}
      />
      <Route
        path="destinations/catalog/:integrationSlug"
        pageName="Integrations Catalog Detail"
        component={IntegrationsCatalogDetailApp}
        breadcrumb={{
          breadcrumbPath: 'destinations/catalog',
          isCatalogSetup: true
        }}
        team={PLATFORM_CX}
      />
      <Route
        path="destinations/catalog/:integrationSlug/edit"
        pageName="Integrations Catalog Detail"
        component={IntegrationsCatalogDetailApp}
        breadcrumb={{
          breadcrumbPath: 'destinations/catalog',
          isCatalogSetup: true
        }}
        team={PLATFORM_CX}
      />

      <Route
        path="destinations/warehouses/:warehouseSlug"
        component={WarehouseSetupApp}
        breadcrumb={{
          breadcrumbPath: 'destinations/catalog',
          isCatalogSetup: true
        }}
        team={PLATFORM_CX}
      />

      <Route
        path="destinations/:destinationMetadataSlug"
        showBreadcrumb
        team={PLATFORM_CX}
      >
        <Route path="overview" component={DestinationOverview} showBreadcrumb />
        <Route
          path="sources/:sourceSlug"
          component={DestinationDetails}
          showBreadcrumb
        >
          <Route path="configuration" component={DestinationConfig} />
          <Redirect from="simulator" to="validator" />
          <Redirect from="validator" to="event-tester" />
          <Route
            path="filters"
            component={DestinationDataTransformations}
            team={ROUTING}
          />
          <Route
            path="event-tester"
            pageName="Destination Simulator"
            component={DestinationDebugger}
            team={TOOLING}
          />
          <Redirect from="deliverability" to="event-delivery" />
          <Redirect from="deliverability/:type" to="event-delivery/:type" />
          <Route
            path="event-delivery"
            component={DestinationEventDelivery}
            ignoreScrollBehavior
          >
            <Route path=":type" component={DestinationErrorSheet} />
          </Route>
        </Route>
      </Route>

      <Redirect
        from="sources/:sourceSlug/overview/destinations/:integrationSlug"
        to="destinations/:integrationSlug/sources/:sourceSlug/configuration"
      />

      <Route
        path="sources"
        pageName="Workspace Sources"
        component={SourcesApp}
        label="List of Sources"
        team={PLATFORM_CX}
      />

      <Route
        path="sources/catalog(/:sourceMetadataSlug)"
        disableAutoPageTracking
        label="Catalog"
        component={SourcesCatalogApp}
        breadcrumb={{
          isCatalog: true
        }}
        ignoreScrollBehavior
        team={PLATFORM_CX}
      />
      <Route
        path="sources/setup/:sourceMetadataSlug(/:step)"
        component={LegacySourceSetup}
        breadcrumb={{
          breadcrumbPath: 'sources/catalog',
          isCatalogSetup: true
        }}
        team={PLATFORM_CX}
      />
      <Route
        path="sources/:sourceSlug/schema/events/:eventId"
        disableAutoPageTracking
        component={SourceSchemaEventProperties}
        showBreadcrumb
        team={PROTOCOLS}
      />
      <Route
        path="sources/:sourceSlug"
        component={SourceApp}
        showBreadcrumb
        team={PLATFORM_CX}
      >
        <Route
          path="connect(/:sourceMetadataSlug)"
          disableAutoPageTracking
          component={SourcesCatalogApp}
          team={SOURCES}
        />
        <IndexRedirect to="overview" />
        <Redirect from="integrations" to="overview" />
        <Redirect
          from="integrations/:integrationSlug"
          to="destinations/:integrationSlug"
        />
        <Route
          path="overview"
          pageName="Source Overview"
          component={SourceOverviewApp}
        >
          <Route
            path="runs/:runId"
            pageName="Source Sync Latest"
            component={SourceOverviewRun}
          />
        </Route>

        <Route
          path="visual-tagger"
          team={GROWTH}
          pageName="Visual Tagger Web"
          component={VisualTaggerV2}
        >
          <IndexRedirect to="home" />
          <Route path="home" component={VisualTaggerHome} />
          <Route path="prerequisites" component={VisualTaggerPrereqs} />
          <Route path="guide" component={VisualTaggerGuide} />
          <Route path="create" component={VisualTaggerCreateTags} />
          <Route path="set-url" component={VisualTaggerEnterURL} />
        </Route>

        <Route path="debugger" component={SourceDebugger} team={PROTOCOLS} />
        <Redirect from="schema2" to="schema" />
        <Redirect from="schema" to="schema/events" />
        <Route
          path="function-errors"
          component={SourceFunctionErrors}
          team={FUNCTIONS}
        />
        <Route path="schema" team={PROTOCOLS}>
          <IndexRedirect to="events" />
          <Route
            path="events"
            disableAutoPageTracking
            component={SourceSchemaEvents}
          />
          <Route
            path="identify"
            disableAutoPageTracking
            component={SourceSchemaIdentifyGroup}
          />
          <Route
            path="group"
            disableAutoPageTracking
            component={SourceSchemaIdentifyGroup}
          />
        </Route>
        <Route path="settings" component={SourceSettingsApp}>
          <IndexRedirect to="basic" />
          <Route
            path="cross-domain-analytics"
            disableAutoPageTracking
            component={SourceSettingsCrossDomainAnalyticsApp}
          />
          <Route
            path="collaborators"
            disableAutoPageTracking
            component={SourceSettingsCollaboratorsApp}
          />
          <Route path="oauth" component={SourceSettingsFacebookAdsApp} />
          <Route path="connection" component={SourceSettingsConnectionApp} />
          <Route path="endpoint" component={SourceSettingsEndpointApp} />
          <Route
            path="schema"
            disableAutoPageTracking
            component={SourceSettingsSchemaConfigApp}
            team={PROTOCOLS}
            hideBackground
          />
          <Route
            path="schema/clear"
            disableAutoPageTracking
            component={SourceSettingsRefreshSchemaApp}
            team={PROTOCOLS}
          />
          <Route
            path="basic"
            disableAutoPageTracking
            component={SourceSettingsBasicApp}
          />
          <Route
            path="move"
            disableAutoPageTracking
            component={SourceSettingsMoveApp}
          />
          <Route
            path="keys"
            disableAutoPageTracking
            component={SourceSettingsKeysApp}
          />
          <Route
            path="sql"
            disableAutoPageTracking
            component={SourceSettingsSqlApp}
          />
          <Route
            path="plugins"
            disableAutoPageTracking
            component={SourcePluginsApp}
          />
          <Route
            path="analytics-js"
            component={SourceSettingsAnalyticsJsApp}
            hideBackground
          />
          <Route
            path="selective-sync"
            component={SourceSettingsSelectiveSyncApp}
          />
        </Route>
      </Route>

      {functionsRoutes}
      {personasRoutes}

      <Route path="warehouses/:warehouseId" showBreadcrumb team={PLATFORM_CX}>
        <Route
          path="sources/:sourceId"
          component={WarehouseSourceApp}
          showBreadcrumb
        >
          <IndexRedirect to="sync-history" />
          <Redirect from="overview" to="sync-history" />
          <Route
            disableAutoPageTracking
            path="sync-history"
            component={WarehouseSourceSyncHistory}
          >
            <Route
              disableAutoPageTracking
              path=":runId"
              component={WarehouseRunSheet}
            />
          </Route>
          <Route
            disableAutoPageTracking
            path="settings"
            component={WarehouseSourceSettings}
          />
        </Route>
        <Route component={WarehouseApp}>
          <IndexRedirect to="overview" />
          <Route
            path="overview"
            disableAutoPageTracking
            component={WarehouseOverview}
          />
          <Route
            path="settings"
            disableAutoPageTracking
            component={WarehouseSettingsIndex}
          >
            <Route
              path="connection"
              disableAutoPageTracking
              component={WarehouseSettingsConnection}
            />
            <Route
              path="maintenance"
              disableAutoPageTracking
              component={WarehouseSettingsMaintenance}
            />
            <Route
              path="sync"
              disableAutoPageTracking
              component={WarehouseSettingsSelectiveSync}
            />
            <Route
              path="sync-schedule"
              disableAutoPageTracking
              component={WarehouseSettingsSyncSchedule}
            />
          </Route>
        </Route>
      </Route>
      <Redirect from="connection-health" to="integration-health" />
      <Route
        path="integration-health"
        label="Integration Health"
        pageName="Integration Health"
        component={ConnectionHealthApp}
        team={GROWTH}
      />
    </Route>
    <Route
      path="/:workspaceSlug/settings/billing/payments/:chargeId"
      pageName="Receipt"
      component={ReceiptApp}
      team={GROWTH}
    />
    <Route path="*" component={NotFound} team={GROWTH} />
  </Router>
)

export default routes
